<template>
  <v-select
    :model-value="modelValue || null"
    :items="selectItems"
    :placeholder="placeholder"
    :disabled="disabled"
    item-title="title"
    item-value="value"
    :variant="outlined ? 'outlined' : 'plain'"
    density="compact"
    hide-details
    class="job-family-select"
    :class="{ 'text-body-1': outlined }"
    :rounded="outlined ? 'lg' : undefined"
    :color="outlined ? 'primary' : undefined"
    :menu-icon="outlined ? '$chevron-down' : undefined"
    :list-props="{ density: 'compact', nav: true }"
    :menu-props="{ contentClass: 'job-family-select-menu' }"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <template #selection>
      <span v-if="modelValue">{{ selectedFamilyLabel }}</span>
    </template>
    <template #item="{ props: itemProps, item }">
      <v-list-subheader
        v-if="item.raw.isGroupHeader"
        class="job-family-group-label"
      >
        {{ item.raw.title }}
      </v-list-subheader>
      <v-list-item
        v-else
        v-bind="itemProps"
        class="job-family-option-item"
        :title="item.raw.title"
      />
    </template>
  </v-select>
</template>

<script setup>
import { computed } from 'vue';
import {
  buildJobFamilySelectItems,
  findJobFamilyByUlid,
} from '@/helpers/jobFamilyEditorItems';

const props = defineProps({
  modelValue: { type: String, default: null },
  placeholder: { type: String, default: 'Job family' },
  disabled: { type: Boolean, default: false },
  outlined: { type: Boolean, default: false },
  jobFamilies: { type: Array, default: () => [] },
  jobFamilyGroups: { type: Array, default: () => [] },
  selectedFamilyName: { type: String, default: '' },
});

defineEmits(['update:modelValue']);

const selectItems = computed(() => buildJobFamilySelectItems({
  jobFamilies: props.jobFamilies,
  jobFamilyGroups: props.jobFamilyGroups,
}));

const selectedFamilyLabel = computed(() => (
  selectItems.value.find((item) => item.value === props.modelValue)?.title
  || findJobFamilyByUlid(props.jobFamilies, props.modelValue)?.name
  || props.selectedFamilyName
));
</script>

<style scoped lang="less">
.job-family-select {
  :deep(.v-field--variant-plain) {
    .v-field__input {
      padding: 0;
      min-height: unset;
      font-size: 12px;
      line-height: 16px;
      opacity: 1;
    }

    .v-field__clearable,
    .v-field__append-inner {
      height: 16px;
      width: 12px;
      padding-top: 2px;

      svg {
        width: 12px;
        height: 12px;
        min-width: 12px;
      }
    }
  }

  :deep(.v-field--variant-outlined) {
    color: @text-darkest;

    .v-field__append-inner .v-icon {
      color: @text-dark;
    }

    .v-select__selection--placeholder {
      color: @text-dark;
      opacity: 1;
    }
  }
}
</style>

<style lang="less">
.job-family-select-menu {
  &.v-overlay__content > .v-list {
    box-shadow: none;
    border: 1px solid @stroke-default;
    border-radius: 4px;
    padding: 0;
    background-color: @bg-default;
    min-width: 0;
  }

  .job-family-group-label {
    padding: 8px 12px 4px 16px;
    min-height: unset;
    font-size: 11px;
    font-weight: 600;
    line-height: 150%;
    color: @text-light;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    pointer-events: none;
    cursor: default;
    opacity: 1;
  }

  .job-family-option-item {
    padding: 8px 12px 8px 20px;
    min-height: unset;

    .v-list-item-title {
      font-size: 13px;
      font-weight: 500;
      line-height: 150%;
      color: @text-dark;
    }
  }
}
</style>
